import { useCallback, useMemo } from 'react';

import { type ScopedPermissions } from '@air/api';
import { type Board, type Library } from '@air/api/types';

import { type GetBoardDragItem, type RearrangableItem } from '~/components/Shared/Drag/dragTypes';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useRearrangeBoards } from '~/swr-hooks/gallery/useGalleryRearrange';
import { useGetSubnavSortValue } from '~/swr-hooks/subnav/useSubnavSort';
import {
  canChangeBoardAssetsOrBoardsPosition,
  canChangeLibraryAssetsOrBoardsPosition,
} from '~/utils/permissions/boardPermissions';

export type OnBoardRearrangeParams = {
  adjacentItem: RearrangableItem;
  boards: Board[];
  parentBoardId: Board['id'] | null;
  board: GetBoardDragItem;
};

export type GetCanRearrangeParams = {
  parentBoardId: Board['id'] | null;
  board?: GetBoardDragItem;
};

export type UseLibraryBoardRearrangeParams = {
  library: Pick<Library, 'id' | 'title' | 'color' | 'icon'>;
  libraryPermissions: ScopedPermissions | undefined;
};

export const useLibraryBoardRearrange = ({ library, libraryPermissions }: UseLibraryBoardRearrangeParams) => {
  const { rearrangeBoards } = useRearrangeBoards();
  const { getSubNavSortValue } = useGetSubnavSortValue();
  const { getBoardPermissions } = useBoardPermissionsCache();

  const onBoardRearrange = useCallback(
    ({ adjacentItem, boards, parentBoardId, board }: OnBoardRearrangeParams) => {
      // Check if library is in custom sort - applies to both root and sub-boards
      const librarySort = getSubNavSortValue(library.id);
      const isLibraryCustomSort = librarySort?.libraryBoardSorts?.[0]?.name === 'customSort';

      if (!isLibraryCustomSort) {
        return;
      }

      const isCrossLibraryMove = board.firstItem?.library?.id !== library.id;

      // Use destination library for cross-library moves, source library for within-library moves
      const destinationLibrary = isCrossLibraryMove ? library : board.firstItem?.library;

      rearrangeBoards({
        boards,
        draggedItem: board,
        adjacentItem,
        parentBoardId,
        library: destinationLibrary,
      });
    },
    [rearrangeBoards, getSubNavSortValue, library.id, library],
  );

  const getCanRearrange = useCallback(
    ({ parentBoardId }: GetCanRearrangeParams) => {
      if (!libraryPermissions) {
        return false;
      }

      // Check if library is in custom sort - applies to both root and sub-boards
      const librarySort = getSubNavSortValue(library.id);
      const isLibraryCustomSort = librarySort?.libraryBoardSorts?.[0]?.name === 'customSort';

      if (!isLibraryCustomSort) {
        return false;
      }

      if (!parentBoardId) {
        return canChangeLibraryAssetsOrBoardsPosition(libraryPermissions);
      }

      return canChangeBoardAssetsOrBoardsPosition(getBoardPermissions(parentBoardId));
    },
    [libraryPermissions, library.id, getSubNavSortValue, getBoardPermissions],
  );

  return useMemo(
    () => ({
      onBoardRearrange,
      getCanRearrange,
    }),
    [onBoardRearrange, getCanRearrange],
  );
};
